import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  createArchiveStore,
  createCopyStore,
  createFriendsStore,
  createHomeStore,
  createInviteInboxStore,
  createManagedProfilesStore,
  createPendingDeleteStore,
  createSettingsStore,
} from "@pack-it-up/viewmodel";
import type { TemplateId, TripId, UserId } from "@pack-it-up/shared";
import { useSessionDeps } from "./sessionDeps.js";
import { useStores } from "./AppContext.js";
import { deviceTimezone } from "../lib/timezone.js";

// Session-scoped stores, keyed to the signed-in user. Built once per userId; the
// streams unsubscribe-and-rebuild when the user changes (StrictMode double-mount in
// dev re-runs the factory, which is fine — the streams are idempotent).
export interface SessionStores {
  home: ReturnType<typeof createHomeStore>;
  archive: ReturnType<typeof createArchiveStore>;
  friends: ReturnType<typeof createFriendsStore>;
  managed: ReturnType<typeof createManagedProfilesStore>;
  settings: ReturnType<typeof createSettingsStore>;
  inviteInbox: ReturnType<typeof createInviteInboxStore>;
  copy: ReturnType<typeof createCopyStore>;
  pendingTemplateDelete: ReturnType<typeof createPendingDeleteStore<TemplateId>>;
  pendingTripDelete: ReturnType<typeof createPendingDeleteStore<TripId>>;
}

const SessionContext = createContext<SessionStores | null>(null);

// `inviteInbox` and `copy` are one-shot writers, not streams, so they own nothing
// to release. The two `pendingDelete` stores own a timer, not a stream, but still
// need clearing on teardown so a stray delete can't fire after the session is gone.
const disposeSession = (stores: SessionStores): void => {
  stores.home.getState().dispose();
  stores.archive.getState().dispose();
  stores.friends.getState().dispose();
  stores.managed.getState().dispose();
  stores.settings.getState().dispose();
  stores.pendingTemplateDelete.getState().dispose();
  stores.pendingTripDelete.getState().dispose();
};

export const SessionProvider = ({ userId, children }: { userId: UserId; children: ReactNode }): ReactNode => {
  const { repos, clock, applyCopyPlan } = useSessionDeps();
  const { auth } = useStores();
  const stores = useMemo<SessionStores>(
    () => ({
      home: createHomeStore({
        trips: repos.trips,
        templates: repos.templates,
        userId,
        today: () => clock.today(deviceTimezone()),
      }),
      archive: createArchiveStore({ trips: repos.trips, templates: repos.templates, userId }),
      friends: createFriendsStore({ friends: repos.friends, userId }),
      managed: createManagedProfilesStore({ managed: repos.managed, userId }),
      settings: createSettingsStore({ profiles: repos.profiles, userId }),
      // listMyInvites (T25) 400s server-side until the account's email is verified;
      // read live off the auth store rather than rebuilding this store when it flips.
      inviteInbox: createInviteInboxStore({
        sharing: repos.sharing,
        isEmailVerified: () => auth.getState().user?.emailVerified ?? false,
      }),
      copy: createCopyStore({ trips: repos.trips, templates: repos.templates, applyCopyPlan }),
      pendingTemplateDelete: createPendingDeleteStore<TemplateId>({ execute: (id) => repos.templates.deleteTemplate(id) }),
      pendingTripDelete: createPendingDeleteStore<TripId>({ execute: (id) => repos.trips.deleteTrip(id) }),
    }),
    [repos, clock, applyCopyPlan, userId, auth],
  );

  // Each store owns its Firestore streams; a superseded set (userId changed, or the
  // repos/clock seam was swapped) must unsubscribe or its listeners keep firing into
  // state nothing renders. Deliberately not an effect *cleanup*: StrictMode's simulated
  // remount runs cleanups without rebuilding the memo, which would leave the live set
  // disposed. Disposing only the previous set is remount-safe.
  const previous = useRef<SessionStores | null>(null);
  useEffect(() => {
    const superseded = previous.current;
    previous.current = stores;
    if (superseded !== null && superseded !== stores) disposeSession(superseded);
  }, [stores]);

  return <SessionContext.Provider value={stores}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionStores => {
  const stores = useContext(SessionContext);
  if (stores === null) throw new Error("useSession must be used within <SessionProvider>");
  return stores;
};
